/**
 * SynAssistant → Schedules, as AgencyAnalytics': Add Schedule (name, one-time
 * or recurring daily / weekly / monthly, the hour, the prompt) and the list.
 * Each run lands in Conversation History and the inbox. Scheduled runs only
 * read; they never change anything.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';

import { assistantApi, type AssistantSchedule } from './assistant-api.ts';

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const FREQUENCY_LABEL: Record<AssistantSchedule['frequency'], string> = {
  ONCE: 'Once',
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
};
const when = new Intl.DateTimeFormat('en-ZA', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

function today(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

export function SchedulesPanel(props: {
  organisationId: string;
  client: { id: string; name: string } | null;
}): ReactNode {
  const [schedules, setSchedules] = useState<AssistantSchedule[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    name: '',
    prompt: '',
    recurring: true,
    frequency: 'WEEKLY' as AssistantSchedule['frequency'],
    run_hour: 8,
    run_on: today(),
  });
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => {
    assistantApi.schedules(props.organisationId).then(setSchedules, () => setSchedules([]));
  }, [props.organisationId]);
  const save = (event: FormEvent) => {
    event.preventDefault();
    setProblem(null);
    assistantApi
      .addSchedule(props.organisationId, {
        name: form.name,
        prompt: form.prompt,
        frequency: form.recurring ? form.frequency : 'ONCE',
        run_hour: form.run_hour,
        run_on: form.run_on || null,
        client_id: props.client?.id ?? null,
      })
      .then((next) => {
        setSchedules(next);
        setAdding(false);
      })
      .catch((error: unknown) =>
        setProblem(error instanceof Error ? error.message : 'The schedule could not be saved.'),
      );
  };
  if (adding)
    return (
      <form className="as-skill-form" onSubmit={save}>
        <p className="kp-faint">
          {props.client ? `About ${props.client.name}.` : 'Agency-wide.'} The answer appears in
          History and your inbox.
        </p>
        <label className="rs-field">
          Name
          <input
            value={form.name}
            maxLength={120}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            required
          />
        </label>
        <div className="rb-seg" role="radiogroup" aria-label="Type">
          {[
            [false, 'One-time'],
            [true, 'Recurring'],
          ].map(([value, label]) => (
            <button
              key={String(value)}
              type="button"
              role="radio"
              aria-checked={form.recurring === value}
              className={form.recurring === value ? 'on' : ''}
              onClick={() => setForm({ ...form, recurring: value as boolean })}
            >
              {label as string}
            </button>
          ))}
        </div>
        <div className="kp-two">
          {form.recurring && (
            <label className="rs-field">
              Repeats
              <select
                value={form.frequency}
                onChange={(event) =>
                  setForm({
                    ...form,
                    frequency: event.target.value as AssistantSchedule['frequency'],
                  })
                }
              >
                <option value="DAILY">Daily</option>
                <option value="WEEKLY">Weekly</option>
                <option value="MONTHLY">Monthly</option>
              </select>
            </label>
          )}
          <label className="rs-field">
            {form.recurring ? 'Starting' : 'On'}
            <input
              type="date"
              value={form.run_on}
              min={today()}
              onChange={(event) => setForm({ ...form, run_on: event.target.value })}
            />
          </label>
          <label className="rs-field">
            At
            <select
              value={form.run_hour}
              onChange={(event) => setForm({ ...form, run_hour: Number(event.target.value) })}
            >
              {HOURS.map((hour) => (
                <option key={hour} value={hour}>
                  {String(hour).padStart(2, '0')}:00
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="rs-field">
          Prompt
          <textarea
            rows={5}
            maxLength={4000}
            value={form.prompt}
            placeholder="For example: summarise last week's results and flag anything unusual."
            onChange={(event) => setForm({ ...form, prompt: event.target.value })}
            required
          />
        </label>
        {problem && <p className="kp-problem">{problem}</p>}
        <div className="kp-dialog-actions">
          <button type="button" className="btn secondary" onClick={() => setAdding(false)}>
            Cancel
          </button>
          <button type="submit" className="btn">
            Save schedule
          </button>
        </div>
      </form>
    );
  return (
    <div className="as-tools">
      <button type="button" className="btn secondary" onClick={() => setAdding(true)}>
        + Add Schedule
      </button>
      <ul className="as-list">
        {(schedules ?? []).map((schedule) => (
          <li key={schedule.id}>
            <span className="as-list-main">
              <strong>{schedule.name}</strong>
              <small>
                {FREQUENCY_LABEL[schedule.frequency]} ·{' '}
                {schedule.next_run_at
                  ? `next ${when.format(new Date(schedule.next_run_at))}`
                  : 'done'}
              </small>
            </span>
            <button
              type="button"
              className="ex-link"
              onClick={() =>
                void assistantApi
                  .removeSchedule(props.organisationId, schedule.id)
                  .then(setSchedules)
              }
            >
              Delete
            </button>
          </li>
        ))}
      </ul>
      {schedules?.length === 0 && (
        <p className="as-empty">
          No schedules yet. Ask for a weekly summary every Monday, for example.
        </p>
      )}
    </div>
  );
}
